import { useState, useEffect, useCallback } from 'react';

import { useEntityCache } from '../contexts/DataCacheContext';

const useData = (loadData, { initialData = null, autoload = true, cache = null } = {}) => {
    // Cache
    const hasCache = cache !== null;
    const [cacheEntityType = null, cacheEntityId = null] = hasCache ? cache : [];
    const [cachedEntity, setCachedEntity] = useEntityCache(cacheEntityType, cacheEntityId);

    const [data, setData] = useState(initialData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const load = useCallback(
        (...args) => {
            let canceled = false;
            setLoading(true);
            const promise = loadData(...args)
                .then((newData) => {
                    if (!canceled) {
                        if (hasCache) {
                            setCachedEntity(newData);
                        } else {
                            setData(newData);
                        }
                        setLoading(false);
                    }
                    return newData;
                })
                .catch((newError) => {
                    setError(newError);
                    setLoading(false);
                });
            promise.cancel = () => {
                canceled = true;
                setLoading(false);
            };
            return promise;
        },
        [loadData, setLoading, setData, hasCache],
    );

    const finalData = hasCache ? cachedEntity : data;
    const needsData = finalData === null;

    useEffect(() => {
        let loader = null;
        if (autoload && needsData) {
            loader = load();
        }
        return () => {
            if (loader !== null) {
                loader.cancel();
            }
        };
    }, [autoload, load, needsData]);
    return {
        data: finalData,
        load,
        loading,
        error,
    };
};

export default useData;
